import React from "react";
import {
  Box,
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckIcon from "@mui/icons-material/Check";

export type TaxType = "GST" | "VAT";

const TAX_TYPES: TaxType[] = ["GST", "VAT"];

interface ITaxTypeSelectorProps {
  value: TaxType;
  onChange: (next: TaxType) => void;
  disabled?: boolean;
}

/**
 * Clickable tax-type trigger ("GST ▾") that opens a small popup to pick between
 * GST and VAT. Styled to sit inside a field's start adornment so it reads like
 * the field's tax label but is interactive.
 */
export const TaxTypeSelector: React.FC<ITaxTypeSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!disabled) setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (next: TaxType) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    if (next !== value) onChange(next);
  };

  return (
    <>
      <Box
        component="span"
        role="button"
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleOpen}
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0,
          fontWeight: 700,
          fontSize: "0.7rem",
          lineHeight: 1,
          color: "text.secondary",
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          px: 0,
          mx: 0,
          "&:hover": disabled ? {} : { color: "text.primary" },
        }}
      >
        {value}
        {!disabled && (
          <ArrowDropDownIcon sx={{ fontSize: 15, ml: "-1px" }} />
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
        PaperProps={{ sx: { minWidth: 120, mt: 0.5 } }}
      >
        {TAX_TYPES.map((type) => (
          <MenuItem
            key={type}
            selected={type === value}
            onClick={handleSelect(type)}
          >
            <ListItemIcon sx={{ minWidth: 28 }}>
              {type === value && <CheckIcon fontSize="small" color="primary" />}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>
              {type}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
